export const policyDocuments = {
  privacy: {
    title: "Privacy Policy",
    summary:
      "This policy explains how Hound & Home collects, uses, shares and protects personal information when you visit houndandhome.xyz, shop with us or contact us.",
    sections: [
      {
        heading: "Who is responsible for your information",
        paragraphs: [
          "Hound & Home is responsible for the personal information described in this policy. You can contact us at packs@houndandhome.xyz about privacy questions or requests.",
          "This policy applies to our website, checkout, customer service and order-related communications. A third-party service linked from our site may apply its own privacy notice to information it controls.",
        ],
      },
      {
        heading: "Information you provide",
        paragraphs: [
          "We may receive your name, email address, telephone number, billing and delivery address, country, order details, product preferences, returns information and messages you send us.",
          "If you buy for another person, you should only give us their details when you have permission to do so. Please do not send sensitive personal information unless we specifically request it for a lawful purpose.",
        ],
      },
      {
        heading: "Information collected through the site",
        paragraphs: [
          "The site may record device and browser information, IP address, approximate location derived from IP, pages viewed, referring pages, timestamps, error logs and interactions with site features. The shopping basket is stored in browser local storage so it can persist between visits on the same device.",
          "Cookies and similar technologies are described in our Cookie Policy. We use optional technologies only where permitted and, when required, after obtaining consent.",
        ],
        links: [{ to: "/cookies", label: "Read the Cookie Policy" }],
      },
      {
        heading: "How we use personal information",
        bullets: [
          "To provide the site, basket and checkout and to fulfil our contract with you.",
          "To confirm, prepare, deliver, cancel, return or refund an order.",
          "To answer enquiries, provide product support and keep service records.",
          "To authenticate transactions, prevent fraud, protect accounts and secure the site.",
          "To maintain business, tax, accounting, warranty and compliance records.",
          "To understand site performance and improve our products and customer experience.",
          "To send marketing only when we have a valid basis and to record opt-out choices.",
        ],
      },
      {
        heading: "Legal grounds",
        paragraphs: [
          "Depending on your location and the activity, we rely on performance of a contract, steps requested before a contract, compliance with legal obligations, our legitimate interests, or your consent. Legitimate interests may include securing the site, preventing fraud, keeping proportionate business records and improving our services. We consider the effect on your rights before relying on those interests.",
          "Where processing is based on consent, you may withdraw it at any time. Withdrawal does not affect processing that was lawful before it was withdrawn.",
        ],
      },
      {
        heading: "Payments and fraud screening",
        paragraphs: [
          "Payment information is sent to the payment service made available at checkout. Hound & Home does not need complete card details to provide customer service and does not intentionally store full card numbers in the website application.",
          "We and our providers may assess transaction, device and delivery information to identify fraud or misuse. A transaction may be delayed, declined or referred for review. You may contact us if you believe a decision was made in error.",
        ],
      },
      {
        heading: "When information is shared",
        paragraphs: [
          "We share only what is reasonably needed with providers supporting website hosting, payments, fraud prevention, order fulfilment, delivery, communications, analytics, customer support and professional advice. Providers must handle information for the agreed purpose and protect it appropriately.",
          "We may also disclose information to comply with law, enforce our terms, protect people or property, investigate suspected wrongdoing, or support a sale or reorganisation of the business. We do not sell personal information for money.",
        ],
      },
      {
        heading: "International processing",
        paragraphs: [
          "Some providers may process information in a country different from yours. Where applicable law requires it, we use recognised safeguards or another lawful transfer mechanism and consider the protection available in the destination country.",
        ],
      },
      {
        heading: "Retention",
        paragraphs: [
          "We keep personal information only as long as reasonably necessary for the purpose collected. Retention periods take account of order fulfilment, return and warranty periods, legal and tax duties, fraud prevention, chargebacks, complaints and potential disputes. We then delete or anonymise the information where reasonably practicable.",
        ],
      },
      {
        heading: "Your rights and choices",
        paragraphs: [
          "Depending on where you live, you may have rights to request access, correction, deletion, restriction, objection, portability or withdrawal of consent, and to complain to a privacy regulator. Some rights have legal exceptions; for example, we may need to retain transaction records required by law.",
          "Send a request to packs@houndandhome.xyz. We may ask for information reasonably needed to verify identity and authority. An authorised agent should provide evidence of authority where required. We will not discriminate against you for making a valid request.",
        ],
      },
      {
        heading: "Marketing communications",
        paragraphs: [
          "You can stop promotional email by using its unsubscribe link or contacting us. We may still send non-marketing messages that are necessary for an order, return, security alert or requested customer service.",
        ],
        links: [
          { to: "/marketing-preferences", label: "Marketing Preferences" },
        ],
      },
      {
        heading: "Children",
        paragraphs: [
          "The site is intended for a general adult shopping audience and is not directed to children. We do not knowingly collect personal information from a child where parental permission is required. A parent or guardian who believes a child has provided information should contact us.",
        ],
      },
      {
        heading: "Security and updates",
        paragraphs: [
          "We use reasonable administrative and technical measures designed to protect personal information. No online system can be guaranteed completely secure, so please use care when sending information online.",
          "We may update this policy when our services, providers or legal duties change. The revised date will appear at the top, and material changes will be highlighted or notified where required.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    summary:
      "These terms govern use of houndandhome.xyz and purchases made from Hound & Home. Mandatory consumer rights that apply to you are not excluded or reduced.",
    sections: [
      {
        heading: "Using the site",
        paragraphs: [
          "You may use the site for lawful personal shopping and product research. You must be legally capable of entering a contract, provide accurate information and avoid conduct that harms the site, other customers or third-party rights.",
          "We may suspend access, remove content or decline service where reasonably necessary for security, legal compliance, suspected fraud or a serious breach of these terms.",
        ],
      },
      {
        heading: "Accounts and checkout information",
        paragraphs: [
          "You are responsible for the accuracy of contact, billing and delivery information submitted at checkout and for keeping any account credentials confidential. Tell us promptly if you suspect unauthorised activity.",
        ],
      },
      {
        heading: "Product information",
        paragraphs: [
          "We take reasonable care with descriptions, dimensions and photographs. Screen settings can affect colour. Natural stones, amber, pearls and hand-finished items may show differences in colour, pattern, texture, inclusions, shape or weight; these characteristics are not faults when they remain consistent with the product description.",
          "Packaging shown in photographs is illustrative unless the product page expressly says it is included.",
        ],
        links: [
          { to: "/product-information", label: "Product Information Policy" },
        ],
      },
      {
        heading: "Availability and order limits",
        paragraphs: [
          "Products are subject to availability. Adding an item to a basket does not reserve it. We may apply reasonable quantity limits and may cancel quantities that appear intended for unauthorised resale.",
        ],
      },
      {
        heading: "Order acceptance",
        paragraphs: [
          "Submitting an order is an offer to buy. An automated acknowledgement confirms receipt, not acceptance. A contract is formed when we send an express acceptance or dispatch confirmation, whichever happens first.",
          "Before acceptance we may decline an order because of availability, delivery restrictions, an obvious price or description error, unsuccessful payment authorisation, suspected fraud or legal restrictions. Any captured amount for a declined order will be released or refunded.",
        ],
      },
      {
        heading: "Prices, taxes and payment",
        paragraphs: [
          "The product price, delivery charge, currency and any taxes collected by us are shown at checkout. You authorise the payment provider to charge the confirmed total. Import duties and destination charges not collected at checkout remain the recipient's responsibility unless stated otherwise.",
          "If a price is obviously incorrect, we may contact you for instructions or cancel before acceptance. We will not increase the accepted price of an order except where you approve a requested change.",
        ],
        links: [
          { to: "/pricing-taxes", label: "Pricing, Taxes & Duties" },
          { to: "/payment-security", label: "Payment & Security" },
        ],
      },
      {
        heading: "Delivery",
        paragraphs: [
          "Delivery options and estimates are shown at checkout or in your confirmation. Estimates are not guarantees where a delay is outside our reasonable control. You must provide a deliverable address and cooperate with reasonable delivery requirements.",
        ],
        links: [{ to: "/shipping", label: "Shipping Policy" }],
      },
      {
        heading: "Cancellation, returns and refunds",
        paragraphs: [
          "Our separate policies explain voluntary return windows, exclusions, cancellation requests and refund processing. Those policies supplement these terms and do not replace rights that cannot legally be waived.",
        ],
        links: [
          { to: "/order-cancellations", label: "Order Changes & Cancellations" },
          { to: "/returns", label: "Returns & Exchanges" },
          { to: "/refunds", label: "Refund Policy" },
        ],
      },
      {
        heading: "Care, warranty and safety",
        paragraphs: [
          "Use products for their intended purpose and follow the care and safety guidance supplied. Normal wear, accidental damage, misuse and failure to follow care instructions are different from a manufacturing defect. Any voluntary warranty is additional to mandatory legal rights.",
        ],
        links: [
          { to: "/jewellery-care", label: "Jewellery Care" },
          { to: "/warranty-repairs", label: "Warranty & Repairs" },
          { to: "/product-safety", label: "Product Safety" },
        ],
      },
      {
        heading: "Offers, gift cards and pre-orders",
        paragraphs: [
          "Where these features are offered, their displayed conditions and the relevant policy form part of the purchase terms. A specific product-page term takes priority over a general policy only where clearly stated and legally permitted.",
        ],
        links: [
          { to: "/promotions", label: "Promotions Policy" },
          { to: "/gift-cards", label: "Gift Card Policy" },
          { to: "/preorders", label: "Pre-order Policy" },
        ],
      },
      {
        heading: "Intellectual property and user content",
        paragraphs: [
          "Site content may be protected by copyright, trade mark and other rights owned by Hound & Home or its licensors. A limited right to view and use the site for personal shopping does not transfer ownership. Content you submit remains subject to the rights and permissions described in our User Content Policy.",
        ],
        links: [
          { to: "/intellectual-property", label: "Intellectual Property Notice" },
          { to: "/user-content", label: "User Content Policy" },
        ],
      },
      {
        heading: "Third-party services",
        paragraphs: [
          "Payment, delivery and external links may be provided by third parties. Their separate terms may apply to the service they control. We are not responsible for an external website merely because we link to it, but this does not remove responsibility we have for our own obligations.",
        ],
      },
      {
        heading: "Liability",
        paragraphs: [
          "Nothing in these terms excludes liability or remedies that cannot lawfully be excluded. Subject to that rule, we are not responsible for losses that were not reasonably foreseeable when the contract was formed, losses caused solely by events outside our reasonable control, or business losses arising from a purchase made as a consumer.",
          "The site is provided with reasonable care, but we do not promise uninterrupted or error-free access. Product and order obligations remain governed by the terms applicable to the accepted order.",
        ],
      },
      {
        heading: "Events outside reasonable control",
        paragraphs: [
          "We are not responsible for delay or failure caused by events we could not reasonably prevent, such as carrier disruption, customs delay, severe weather, network failure, industrial action or government action. We will take reasonable steps to reduce the effect and will contact you when appropriate.",
        ],
      },
      {
        heading: "Changes, severability and enforcement",
        paragraphs: [
          "We may update these terms prospectively. The version in force when an order is accepted continues to govern that order unless law requires otherwise. If one term is unenforceable, the remaining terms continue. A delay in enforcing a right is not a waiver.",
          "Applicable law and forum are subject to mandatory consumer protections. Before starting formal proceedings, please contact us so we can try to resolve the matter fairly.",
        ],
      },
    ],
  },
  cookies: {
    title: "Cookie & Local Storage Policy",
    summary:
      "This policy explains how houndandhome.xyz uses cookies, browser local storage and similar technologies, and the choices available to visitors.",
    sections: [
      {
        heading: "What these technologies are",
        paragraphs: [
          "Cookies are small text files stored by a browser. Local storage is browser-based storage that can retain data on a device without using a cookie. Pixels, tags and software development kits can perform related functions. In this policy, we refer to these collectively as browser technologies.",
        ],
      },
      {
        heading: "Strictly necessary use",
        paragraphs: [
          "Necessary technologies support core features such as security, navigation, checkout and fraud prevention. The site currently uses local storage to remember basket contents on the device. These features may not work correctly if the related storage is blocked or cleared.",
        ],
      },
      {
        heading: "Functional use",
        paragraphs: [
          "Where enabled, functional technologies remember choices such as display, region or accessibility preferences. They are not required for the basic page to load, although disabling them may reduce convenience.",
        ],
      },
      {
        heading: "Analytics",
        paragraphs: [
          "If analytics is enabled, it may help us understand visits, page performance, errors and general interaction patterns. Where law requires consent, non-essential analytics will be used only after that consent is given.",
        ],
      },
      {
        heading: "Advertising and social media",
        paragraphs: [
          "If advertising, affiliate or social-media technologies are introduced, the consent interface will identify their purpose and provide legally required controls. We do not treat this policy as permission to place a non-essential technology where opt-in consent is required.",
        ],
      },
      {
        heading: "First-party and third-party technologies",
        paragraphs: [
          "A first-party technology is set by houndandhome.xyz. A third-party technology is set by another service, such as a payment or analytics provider. Third parties may handle information under their own notices when acting independently.",
        ],
      },
      {
        heading: "Duration",
        paragraphs: [
          "Session technologies expire when the browser session ends. Persistent technologies remain until their stated expiry or until cleared. Retention can vary by feature and provider; any available consent manager is the most current source of specific names, providers and durations.",
        ],
      },
      {
        heading: "Your controls",
        paragraphs: [
          "You can use any cookie settings displayed on the site and can block or delete browser data through your browser settings. Clearing local storage removes the saved basket on that device. Consent can be withdrawn as easily as it was given, without affecting earlier lawful use.",
        ],
      },
      {
        heading: "Updates",
        paragraphs: [
          "We will update this policy if our use of browser technologies materially changes. Our Privacy Policy explains how related personal information is handled.",
        ],
        links: [{ to: "/privacy", label: "Read the Privacy Policy" }],
      },
    ],
  },
  accessibility: {
    title: "Accessibility Statement",
    summary:
      "Hound & Home wants houndandhome.xyz to be usable by as many people as reasonably possible, including people who use assistive technologies.",
    sections: [
      {
        heading: "Our approach",
        paragraphs: [
          "We aim for clear navigation, readable text, keyboard-operable controls, visible focus, meaningful page structure, useful image alternatives and forms with understandable labels and errors.",
        ],
      },
      {
        heading: "Ongoing improvement",
        paragraphs: [
          "Accessibility is an ongoing process. Content, third-party features and technology can change, so we review issues and prioritise fixes according to their effect on access to products, information and checkout.",
        ],
      },
      {
        heading: "Third-party content",
        paragraphs: [
          "Some payment, map, embedded or delivery features may be controlled by third parties. We will raise material barriers with the provider where we cannot fix them directly and will try to offer a reasonable alternative.",
        ],
      },
      {
        heading: "Requesting help or an alternative format",
        paragraphs: [
          "Email packs@houndandhome.xyz with the page address, the task you were trying to complete and the barrier encountered. If comfortable, include your browser, device and assistive technology. Do not include passwords or full payment details.",
          "We will acknowledge the report and make reasonable efforts to provide the information or service another way while the issue is investigated.",
        ],
      },
    ],
  },
  shipping: {
    title: "Shipping & Delivery Policy",
    summary:
      "This policy explains order processing, delivery estimates, tracking, address responsibilities and international shipping for Hound & Home orders.",
    sections: [
      {
        heading: "Available services and charges",
        paragraphs: [
          "Available delivery services, prices and current estimates are shown at checkout for the delivery address entered. A service is not confirmed until the order is accepted. We may restrict certain products or destinations for legal, carrier or security reasons.",
        ],
      },
      {
        heading: "Processing",
        paragraphs: [
          "Orders are prepared on working days. Processing time is separate from carrier transit time. Orders placed during promotions, public holidays or high-volume periods may take longer. If a made-to-order or pre-order item has a different lead time, the product page or confirmation will state it.",
        ],
      },
      {
        heading: "Dispatch and tracking",
        paragraphs: [
          "We send a dispatch message when an order leaves our control. Tracking is provided where the selected service supports it. A tracking scan can take time to appear and is maintained by the carrier.",
        ],
      },
      {
        heading: "Delivery estimates",
        paragraphs: [
          "Delivery dates are estimates unless we expressly agree a guaranteed date. Customs, weather, industrial action, remote-area service, seasonal demand and other events outside reasonable control can cause delay. Please allow the estimated period to pass before reporting a parcel as late.",
        ],
      },
      {
        heading: "Address accuracy and changes",
        paragraphs: [
          "You are responsible for a complete, deliverable address. Contact us immediately if it is wrong. We cannot guarantee an amendment after fulfilment begins and may charge the direct cost of redelivery or return caused by an incorrect address, where legally permitted.",
        ],
      },
      {
        heading: "Failed or refused delivery",
        paragraphs: [
          "If delivery is attempted, follow the carrier's instructions within its collection or rearrangement period. A parcel returned because it was unclaimed, refused or incorrectly addressed may be refunded less non-recoverable delivery and return costs where permitted by law.",
        ],
      },
      {
        heading: "International orders",
        paragraphs: [
          "International availability depends on destination. The recipient is responsible for import duties, taxes, brokerage or customs charges not collected at checkout. Customs authorities may open or inspect a parcel and may require local identification or payment before release.",
        ],
      },
      {
        heading: "Split shipments",
        paragraphs: [
          "We may send items separately when this helps fulfil an order. You will not be charged an additional delivery fee solely because we choose to split the shipment.",
        ],
      },
      {
        heading: "Missing, delayed or damaged parcels",
        paragraphs: [
          "Check the address, tracking, safe place and household members before contacting us. Report visible parcel damage promptly and keep the packaging while we investigate. We may ask for photographs or a non-receipt confirmation and may need to complete a carrier investigation before a replacement or refund.",
          "Nothing in this process reduces rights you have under applicable consumer law.",
        ],
      },
    ],
  },
  returns: {
    title: "Returns & Exchanges Policy",
    summary:
      "Eligible Hound & Home products may be returned within 30 days after delivery under this voluntary policy, in addition to any mandatory rights you have.",
    sections: [
      {
        heading: "Return window",
        paragraphs: [
          "Contact us within 30 days after delivery to request a return. The item should then be sent using the instructions and return address we provide. If local law gives you a longer period or additional cancellation right, that law takes priority.",
        ],
      },
      {
        heading: "Return condition",
        paragraphs: [
          "Items must be unworn, unused and in resalable condition, with their original tags, protective components and included accessories. Please package jewellery securely. You may inspect an item as you reasonably would in a shop, but we may make a lawful deduction for handling beyond what is necessary to assess its nature and characteristics.",
        ],
      },
      {
        heading: "Items that may be excluded",
        bullets: [
          "Personalised, engraved, resized or otherwise made-to-specification items, except where faulty or where law requires a return.",
          "Earrings or other hygiene-sensitive items after a hygiene seal has been removed, where the law permits this exclusion.",
          "Gift cards, except where required by law or stated in the Gift Card Policy.",
          "Items marked final sale only where that restriction is lawful and clearly shown before purchase.",
        ],
      },
      {
        heading: "Starting a return",
        paragraphs: [
          "Email packs@houndandhome.xyz with the order number, item and reason for return. Wait for return instructions before posting. Do not send an item to an address found elsewhere on the site unless the return instructions confirm it.",
        ],
      },
      {
        heading: "Return shipping",
        paragraphs: [
          "For a change-of-mind return, you are normally responsible for secure return postage unless we state otherwise. Use a tracked service and keep proof of posting; the item remains your responsibility until received. If an item is faulty, damaged on arrival or incorrect, contact us before sending it so we can provide the appropriate remedy and instructions.",
        ],
      },
      {
        heading: "Exchanges",
        paragraphs: [
          "Exchanges depend on stock availability. The quickest option may be to return the original item and place a new order. Any price difference and additional delivery cost will be confirmed before an exchange is completed.",
        ],
      },
      {
        heading: "Inspection and outcome",
        paragraphs: [
          "We inspect returned items and email the outcome. An item that does not meet the conditions may be returned to you or receive a reduced refund where permitted by law. Approved refunds are processed under our Refund Policy.",
        ],
        links: [{ to: "/refunds", label: "Read the Refund Policy" }],
      },
      {
        heading: "Gifts and bundles",
        paragraphs: [
          "A refund goes to the original payment method unless law or the original purchase arrangement requires otherwise. If returning part of a bundle means a discount no longer applies, the refund may reflect the price of the retained items at their applicable standalone price, where disclosed and legally permitted.",
        ],
      },
      {
        heading: "Mandatory rights",
        paragraphs: [
          "This voluntary policy does not limit remedies for goods that are faulty, misdescribed or otherwise non-conforming, nor any statutory cooling-off right that applies to a distance sale.",
        ],
      },
    ],
  },
  refunds: {
    title: "Refund Policy",
    summary:
      "This policy describes how approved refunds, partial refunds, delivery charges and payment timing are handled.",
    sections: [
      {
        heading: "When a refund is issued",
        paragraphs: [
          "A refund may follow an approved return, accepted cancellation, unavailable item, duplicate charge, delivery failure for which we are responsible, or another remedy required by law.",
        ],
      },
      {
        heading: "Refund method",
        paragraphs: [
          "Refunds are normally sent to the original payment method. We may ask for an alternative method only when the original method cannot receive the refund or where law permits. We will not ask you to share a full card number by email.",
        ],
      },
      {
        heading: "Processing time",
        paragraphs: [
          "We aim to authorise an approved refund within 10 business days after receiving and checking a return, or after confirming a cancellation that does not require a return. Your bank or payment provider may need additional time to post the credit.",
        ],
      },
      {
        heading: "Delivery charges",
        paragraphs: [
          "Original standard delivery charges are refunded where required by applicable cancellation law. Premium upgrades, international duties and charges paid directly to third parties are not refundable by us unless law requires it or the service was not provided because of our breach.",
        ],
      },
      {
        heading: "Partial refunds and deductions",
        paragraphs: [
          "Where legally permitted, a refund may be reduced for missing parts, damage, use or handling beyond what is reasonably needed to inspect the product, or direct costs caused by an unclaimed or incorrectly addressed parcel. We will explain any deduction.",
        ],
      },
      {
        heading: "Promotions and gift cards",
        paragraphs: [
          "Refunds reflect the amount actually paid after discounts. The promotional value of a code is not paid out in cash. Purchases made with a gift card are normally credited back to a gift card balance, subject to applicable law.",
        ],
      },
      {
        heading: "Late or missing refunds",
        paragraphs: [
          "If our confirmation has arrived but the credit is not visible, first check the original payment account and then contact the provider. If it remains missing after the provider's expected posting period, email us with the order number; do not send full payment credentials.",
        ],
      },
    ],
  },
  "order-cancellations": {
    title: "Order Changes & Cancellations",
    summary:
      "Contact us as quickly as possible if an order needs to be corrected or cancelled. A request is not confirmed until Hound & Home accepts it.",
    sections: [
      {
        heading: "How to request a change",
        paragraphs: [
          "Email packs@houndandhome.xyz with the order number and requested change. For security, we may ask you to confirm information already associated with the order.",
        ],
      },
      {
        heading: "Before fulfilment",
        paragraphs: [
          "We will try to change an address, variant, quantity or cancellation before fulfilment begins, but cannot guarantee it. A product or price change may require cancellation and a new order.",
        ],
      },
      {
        heading: "After dispatch",
        paragraphs: [
          "Once dispatched, an order usually cannot be recalled or redirected. You may use the applicable return or statutory cancellation process after delivery. Refusing a parcel is not always a valid cancellation and may cause extra carrier costs.",
        ],
      },
      {
        heading: "Personalised and made-to-order goods",
        paragraphs: [
          "Work may begin shortly after acceptance. Cancellation rights can be limited for goods made to specification, but this does not remove remedies for faulty or misdescribed goods.",
        ],
      },
      {
        heading: "Cancellation by us",
        paragraphs: [
          "We may cancel before fulfilment because of unavailable stock, an obvious pricing error, delivery restrictions, failed payment, suspected fraud or a legal requirement. We will notify you and release or refund amounts captured for the cancelled goods.",
        ],
      },
    ],
  },
  "payment-security": {
    title: "Payment & Checkout Security Policy",
    summary:
      "This policy explains payment authorisation, checkout security and how to report a suspicious charge.",
    sections: [
      {
        heading: "Payment methods",
        paragraphs: [
          "The methods currently accepted are those displayed at checkout. Availability may depend on device, currency, order value and location. A displayed method can be withdrawn before an order is accepted.",
        ],
      },
      {
        heading: "Authorisation and capture",
        paragraphs: [
          "Your provider may reserve funds when an order is placed. A pending authorisation is not necessarily a completed charge. If an order is declined or cancelled, the provider controls how quickly a reservation disappears.",
        ],
      },
      {
        heading: "Secure processing",
        paragraphs: [
          "Payment credentials are submitted to the payment service presented at checkout. We use reasonable safeguards around the checkout flow and do not ask customers to email passwords, one-time codes or complete card numbers.",
        ],
      },
      {
        heading: "Verification and fraud prevention",
        paragraphs: [
          "A provider may use address checks, security codes, device signals or additional authentication. We may contact you to verify an order, delay fulfilment during review, limit quantities or cancel a transaction where fraud or misuse is reasonably suspected.",
        ],
      },
      {
        heading: "Payment problems",
        paragraphs: [
          "A payment can fail because of provider controls, incorrect information, insufficient funds, authentication failure or a technical issue. Check the entered information and contact the payment provider if needed. Repeated attempts can create multiple temporary authorisations.",
        ],
      },
      {
        heading: "Suspicious charges and phishing",
        paragraphs: [
          "If you do not recognise a charge, contact your payment provider promptly and email us with the date, amount and any Hound & Home order reference. Never send us a full card number. Messages requesting payment outside the checkout should be treated cautiously and verified with us.",
        ],
      },
    ],
  },
  "pricing-taxes": {
    title: "Pricing, Taxes & Duties Policy",
    summary:
      "This policy explains displayed prices, currency, discounts, taxes and destination charges.",
    sections: [
      {
        heading: "Displayed prices",
        paragraphs: [
          "Prices are shown in the currency stated on the product page and checkout. The checkout total controls if another page contains an outdated or cached display, subject to correction of an obvious error before acceptance.",
        ],
      },
      {
        heading: "Taxes",
        paragraphs: [
          "Tax treatment depends on the delivery destination and applicable rules. Taxes collected by us will be included in or added to the checkout total as shown. A customer buying for business purposes is responsible for determining any separate reporting or recovery entitlement.",
        ],
      },
      {
        heading: "Import duties and destination fees",
        paragraphs: [
          "Unless checkout expressly states that duties are included, the recipient is responsible for customs duty, import tax, brokerage, handling and other destination charges. These amounts are set by local authorities or carriers and are outside our control.",
        ],
      },
      {
        heading: "Price changes and promotions",
        paragraphs: [
          "Prices may change prospectively. A later reduction does not automatically change an accepted order. Promotional prices apply only during the stated period and while qualifying stock remains available.",
        ],
      },
      {
        heading: "Obvious errors",
        paragraphs: [
          "If a reasonable customer would recognise that a price or discount is clearly wrong, we may correct it before acceptance and ask whether you wish to proceed. If payment was captured and the order is cancelled, the captured amount will be refunded.",
        ],
      },
    ],
  },
  "product-information": {
    title: "Product Information & Natural Materials Policy",
    summary:
      "Hound & Home product pages describe the design and materials of each piece. Natural and hand-finished characteristics can make individual items unique.",
    sections: [
      {
        heading: "Photographs and screens",
        paragraphs: [
          "We aim to present products clearly, but lighting, scale, cropping and screen settings can affect appearance. Images may be enlarged to show detail and are not a substitute for listed measurements.",
        ],
      },
      {
        heading: "Natural variation",
        paragraphs: [
          "Stone, amber, pearl, shell and other natural materials can vary in colour, veining, translucency, texture, inclusions, outline and weight. These individual characteristics are expected unless they conflict with the description or affect safe use.",
        ],
      },
      {
        heading: "Dimensions and weight",
        paragraphs: [
          "Measurements and weights described as approximate may have small manufacturing tolerances. Ask us before purchase if a particular dimension is essential to your intended use.",
        ],
      },
      {
        heading: "Materials and sensitivities",
        paragraphs: [
          "Material information is provided on the product page where known. Individual skin sensitivities vary, and a description of metal content is not medical advice. Stop wearing an item if irritation occurs and seek appropriate advice where necessary.",
        ],
      },
      {
        heading: "Packaging and styling items",
        paragraphs: [
          "Props, chains, stands and other styling items shown in photography are not included unless the product description says so. Packaging may change while maintaining suitable protection.",
        ],
      },
    ],
  },
  "jewellery-care": {
    title: "Jewellery Care Policy",
    summary:
      "Thoughtful wear, cleaning and storage can help preserve jewellery. Care requirements differ by metal, finish and stone.",
    sections: [
      {
        heading: "Everyday wear",
        paragraphs: [
          "Remove jewellery before bathing, swimming, exercise, sleep and activities involving impact or abrasion. Put jewellery on after perfume, cosmetics, sunscreen and hair products have dried.",
        ],
      },
      {
        heading: "Water and chemicals",
        paragraphs: [
          "Avoid chlorine, salt water, cleaning products, alcohol-based products, sulfur compounds and prolonged moisture. Chemicals can affect metal finishes, adhesives, porous stones and stringing materials.",
        ],
      },
      {
        heading: "Cleaning",
        paragraphs: [
          "Use a soft, dry, non-abrasive cloth unless product-specific guidance says otherwise. Do not use ultrasonic, steam or chemical cleaners on a piece unless a qualified jeweller confirms they are suitable for every material in that piece.",
        ],
      },
      {
        heading: "Storage",
        paragraphs: [
          "Store pieces separately in a cool, dry place to reduce scratching, tangling and tarnish. Close clasps and lay chains flat. Keep jewellery away from direct sunlight, high humidity and rapid temperature changes.",
        ],
      },
      {
        heading: "Plated and oxidised finishes",
        paragraphs: [
          "Plating and deliberately oxidised finishes change gradually with wear. Friction, skin chemistry and cleaning can accelerate that change. This is ordinary wear rather than a manufacturing defect unless the product description or an applicable warranty says otherwise.",
        ],
      },
      {
        heading: "Inspection",
        paragraphs: [
          "Check clasps, settings, jump rings and stringing periodically. Stop wearing a piece if a stone moves, a part bends or a clasp becomes unreliable. Contact us before attempting a repair that may affect a warranty assessment.",
        ],
      },
    ],
  },
  "warranty-repairs": {
    title: "Warranty & Repairs Policy",
    summary:
      "This policy explains how Hound & Home assesses possible manufacturing defects and repair requests. It does not reduce mandatory consumer rights.",
    sections: [
      {
        heading: "Manufacturing issues",
        paragraphs: [
          "If you believe an item has a manufacturing fault, stop using it and contact us with the order number, a description and clear photographs. We may need to inspect the item before confirming a remedy.",
        ],
      },
      {
        heading: "What is not a manufacturing defect",
        bullets: [
          "Normal tarnish, patina or gradual change to plated and oxidised finishes.",
          "Scratches, dents, stretching, broken chains or lost stones caused by impact, snagging, pressure or accidental damage.",
          "Damage caused by water, chemicals, unsuitable cleaning, modification or failure to follow care guidance.",
          "Expected variation in natural materials or ordinary wear of cords, clasps and other moving parts.",
        ],
      },
      {
        heading: "Assessment and remedies",
        paragraphs: [
          "Where an item is confirmed faulty, the available remedy may be repair, replacement, price reduction or refund, depending on the circumstances and applicable law. We will explain any proposed remedy and timing before proceeding.",
        ],
      },
      {
        heading: "Non-warranty repairs",
        paragraphs: [
          "We may be able to quote for repair of accidental damage or wear. Availability depends on parts, materials and technical feasibility. No paid work begins until you accept the quote, delivery cost and estimated timing.",
        ],
      },
      {
        heading: "Unauthorised repair",
        paragraphs: [
          "A repair or alteration by another party may affect our ability to assess the original condition. It does not remove rights unrelated to that work, but we are not responsible for damage caused by an unauthorised repair or modification.",
        ],
      },
    ],
  },
  "gift-cards": {
    title: "Gift Card Policy",
    summary:
      "This policy applies only when Hound & Home gift cards or store credit are offered for sale or issued to a customer.",
    notice:
      "Gift card availability, value, expiry and redemption details shown at purchase or issue form part of this policy.",
    sections: [
      {
        heading: "Using a gift card",
        paragraphs: [
          "A valid card can be used for eligible products through the checkout where the option is displayed. Unless stated otherwise, it cannot be used to buy another gift card, exchanged for cash or applied to an earlier purchase, except where law requires.",
        ],
      },
      {
        heading: "Balance and split payment",
        paragraphs: [
          "If the order total exceeds the available balance, the remainder must be paid using an accepted method. An unused balance remains available subject to any lawful expiry term disclosed when the card was issued.",
        ],
      },
      {
        heading: "Delivery and recipient details",
        paragraphs: [
          "For an electronic card, the purchaser is responsible for entering the correct recipient email address. Contact us promptly if an error is discovered; delivery or use cannot always be reversed.",
        ],
      },
      {
        heading: "Loss, theft and misuse",
        paragraphs: [
          "Treat a card code like cash. We may replace a lost code only when we can verify the purchaser and remaining balance. We may suspend a card reasonably suspected of fraud, duplication or unauthorised resale while investigating.",
        ],
      },
      {
        heading: "Returns and refunds",
        paragraphs: [
          "A refund for goods bought with a gift card is normally returned to gift card balance. Gift card purchase refunds and expiry are subject to mandatory local law and the conditions disclosed before purchase.",
        ],
      },
    ],
  },
  promotions: {
    title: "Promotions & Discount Codes Policy",
    summary:
      "This policy applies to discount codes, sale events, bundles, gifts with purchase and other Hound & Home promotions.",
    sections: [
      {
        heading: "Promotion terms",
        paragraphs: [
          "Each promotion may have a period, eligible products, minimum spend, territory, customer limit or other conditions shown with the offer. Those specific conditions apply together with this policy.",
        ],
      },
      {
        heading: "Using a code",
        paragraphs: [
          "A code must be entered before checkout is completed and cannot normally be applied retrospectively. Unless expressly allowed, promotions cannot be combined, transferred, sold or redeemed for cash.",
        ],
      },
      {
        heading: "Availability and exclusions",
        paragraphs: [
          "Promotions are subject to stock and may exclude gift cards, delivery, repairs, customised items, new releases or specified collections. Meeting a threshold does not reserve stock.",
        ],
      },
      {
        heading: "Returns",
        paragraphs: [
          "A refund is based on the amount paid. If a return means the order no longer qualifies for a bundle, threshold discount or free gift, we may adjust the refund or require the gift to be returned where this was disclosed and is legally permitted.",
        ],
      },
      {
        heading: "Misuse and errors",
        paragraphs: [
          "We may refuse or cancel a promotion affected by fraud, technical manipulation or an obvious publishing error. We will not withdraw a valid accepted benefit contrary to mandatory law.",
        ],
      },
    ],
  },
  preorders: {
    title: "Pre-orders & Back-orders Policy",
    summary:
      "This policy applies only to products clearly identified as pre-order, back-order or made available before stock is ready to dispatch.",
    sections: [
      {
        heading: "Estimated availability",
        paragraphs: [
          "The product page will state an estimated dispatch period. It is an estimate, not a guaranteed date, because production and inbound delivery can change. We will provide a material update if the estimate changes substantially.",
        ],
      },
      {
        heading: "Payment",
        paragraphs: [
          "Checkout will indicate whether payment is taken at order or later. Your provider may apply its own authorisation timing. Pre-order status does not guarantee acceptance if payment or verification later fails.",
        ],
      },
      {
        heading: "Mixed orders",
        paragraphs: [
          "An order containing ready-stock and pre-order items may be held until all items are available or dispatched separately. The expected method should be shown at checkout or confirmed after order review.",
        ],
      },
      {
        heading: "Cancellation",
        paragraphs: [
          "Request cancellation as soon as possible. Cancellation may be limited for personalised or made-to-specification goods after work begins, subject always to mandatory rights. If we cannot supply the item, we will cancel and refund amounts captured for it.",
        ],
      },
    ],
  },
  "fraud-prevention": {
    title: "Fraud Prevention Policy",
    summary:
      "Hound & Home uses proportionate checks to protect customers, payment providers and the business from fraud and misuse.",
    sections: [
      {
        heading: "Checks we may perform",
        paragraphs: [
          "We or our providers may compare order, payment, device, contact and delivery signals; request payment authentication; apply quantity limits; or ask the purchaser to verify limited information.",
        ],
      },
      {
        heading: "Order review",
        paragraphs: [
          "An order may be paused while a check is completed. We may decline or cancel where risk cannot reasonably be resolved. We will never ask by email for your password, one-time banking code or complete card number.",
        ],
      },
      {
        heading: "Protecting genuine customers",
        paragraphs: [
          "A customer who believes a legitimate order was incorrectly stopped can contact us for review. We may be unable to reveal confidential detection criteria because that could weaken security.",
        ],
      },
      {
        heading: "Reporting misuse",
        paragraphs: [
          "Tell your payment provider immediately about an unauthorised charge, then contact us with non-sensitive transaction details. Report fake Hound & Home websites, accounts or messages to packs@houndandhome.xyz.",
        ],
      },
    ],
  },
  "product-safety": {
    title: "Product Safety & Recall Policy",
    summary:
      "This policy provides general safety guidance and explains what to do if a Hound & Home item appears unsafe or is subject to a corrective action.",
    sections: [
      {
        heading: "Safe use",
        paragraphs: [
          "Jewellery is not a toy. Keep small parts, chains and packaging away from babies, young children and pets. Use each item only as jewellery and stop using it if a component becomes loose, sharp, cracked or damaged.",
        ],
      },
      {
        heading: "Skin reactions",
        paragraphs: [
          "Stop wearing an item if irritation occurs. Clean the affected area and seek medical advice if symptoms are significant or persist. Contact us with the product and order details so the issue can be recorded and assessed.",
        ],
      },
      {
        heading: "Reporting a safety concern",
        paragraphs: [
          "Email us promptly with the order number, product name, photographs, a description of what happened and whether anyone was injured. Preserve the item and packaging, and do not attempt a repair before we respond.",
        ],
      },
      {
        heading: "Corrective action and recalls",
        paragraphs: [
          "If a safety issue requires repair, replacement, return or recall, we will publish or send instructions appropriate to the risk and available customer records. Follow those instructions even if the item appears unaffected.",
        ],
      },
    ],
  },
  "intellectual-property": {
    title: "Intellectual Property Notice",
    summary:
      "This notice explains permitted use of original Hound & Home branding and website content and how to report a rights concern.",
    sections: [
      {
        heading: "Protected content",
        paragraphs: [
          "Original logos, names, product photography, text, graphics, page design and other materials may be protected by copyright, trade mark, design or related rights owned by Hound & Home or used with permission.",
        ],
      },
      {
        heading: "Limited personal use",
        paragraphs: [
          "You may view the site, make purchases and save or print order information for personal records. No other licence is granted merely because content is publicly accessible.",
        ],
      },
      {
        heading: "Restricted use",
        paragraphs: [
          "Without prior written permission, you may not reproduce, republish, sell, remove rights notices from, create misleading derivatives of, or use site content to suggest endorsement. Lawful quotation, criticism and other statutory exceptions remain unaffected.",
        ],
      },
      {
        heading: "Third-party rights",
        paragraphs: [
          "Third-party names, technology and payment marks belong to their respective owners. Their appearance does not transfer ownership or imply a partnership beyond the service described.",
        ],
      },
      {
        heading: "Reporting a concern",
        paragraphs: [
          "A rights owner or authorised representative can email the work or right concerned, the exact site location, contact details, the basis of the claim and a statement of authority. We will review a sufficiently detailed notice in good faith.",
        ],
      },
    ],
  },
  "acceptable-use": {
    title: "Website Acceptable Use Policy",
    summary:
      "This policy sets reasonable rules for accessing houndandhome.xyz and interacting with its features.",
    sections: [
      {
        heading: "Permitted use",
        paragraphs: [
          "You may browse, shop, contact us and use available features for lawful personal or authorised business purposes in accordance with our terms.",
        ],
      },
      {
        heading: "Prohibited conduct",
        bullets: [
          "Introducing malware, harmful code, denial-of-service activity or attempts to bypass security.",
          "Using another person's identity or payment method without authority.",
          "Automated extraction that materially burdens the site, circumvents controls or infringes rights.",
          "Testing vulnerabilities without written permission or attempting to access non-public systems or data.",
          "Submitting unlawful, threatening, deceptive, discriminatory or infringing content.",
          "Using site content or data to create confusion about source, sponsorship or affiliation.",
        ],
      },
      {
        heading: "Security research",
        paragraphs: [
          "If you discover a possible vulnerability, stop any testing that could access data or disrupt service and report it privately to packs@houndandhome.xyz with enough detail to reproduce it safely.",
        ],
      },
      {
        heading: "Enforcement",
        paragraphs: [
          "We may restrict access, cancel affected orders, preserve evidence or report conduct when reasonably necessary to protect users, the site or legal rights. Our response will take account of seriousness, intent and applicable law.",
        ],
      },
    ],
  },
  complaints: {
    title: "Complaints & Dispute Resolution Policy",
    summary:
      "We aim to resolve concerns fairly, clearly and without unnecessary delay.",
    sections: [
      {
        heading: "How to complain",
        paragraphs: [
          "Email packs@houndandhome.xyz with your name, order number if applicable, a clear description of the issue, the outcome you are seeking and any relevant photographs or prior correspondence. Do not send passwords or full payment details.",
        ],
      },
      {
        heading: "Acknowledgement and review",
        paragraphs: [
          "We will acknowledge a complaint within a reasonable time and may ask focused questions needed to investigate. We will review order records, policy, product evidence and applicable consumer rights before responding.",
        ],
      },
      {
        heading: "Our response",
        paragraphs: [
          "We will explain the outcome and any proposed remedy or next step. Complex matters involving carriers, payment providers or technical inspection can take longer; if so, we will provide an update rather than leave the complaint unanswered.",
        ],
      },
      {
        heading: "Escalation",
        paragraphs: [
          "If you disagree with the first response, reply with the unresolved point and any new evidence and ask for a further review. You may also use any regulator, consumer body, alternative dispute process or court available under the law that applies to you.",
        ],
      },
      {
        heading: "No retaliation",
        paragraphs: [
          "Making a genuine complaint does not affect your right to fair service. We may, however, limit communications that are abusive, threatening or persistently repetitive while preserving an appropriate channel for the underlying issue.",
        ],
      },
    ],
  },
  "marketing-preferences": {
    title: "Marketing Communications Policy",
    summary:
      "This policy explains how Hound & Home manages promotional messages and your communication choices.",
    sections: [
      {
        heading: "When we send marketing",
        paragraphs: [
          "We send promotional messages only when we have a lawful basis to do so. Depending on location, this may be consent or a limited existing-customer basis permitted by law. Service messages about an order are not marketing.",
        ],
      },
      {
        heading: "Your choices",
        paragraphs: [
          "Use an email unsubscribe link or contact packs@houndandhome.xyz to opt out. A preference change may take a short time to apply to campaigns already prepared, but we will respect it within the period required by law.",
        ],
      },
      {
        heading: "Preference records",
        paragraphs: [
          "We may retain minimal information showing that an address opted out so it is not accidentally added again. This suppression record is used to honour your choice, not to send marketing.",
        ],
      },
      {
        heading: "Third-party marketing",
        paragraphs: [
          "We do not give another business permission to use your contact details for its own unrelated marketing unless you have separately agreed or law otherwise permits it with appropriate notice.",
        ],
      },
    ],
  },
  "user-content": {
    title: "Reviews & User Content Policy",
    summary:
      "This policy applies if you submit a review, photograph, comment, testimonial or other content to Hound & Home.",
    sections: [
      {
        heading: "Your responsibility",
        paragraphs: [
          "Submit only content you are entitled to share. It must reflect a genuine experience and must not reveal another person's private information, infringe rights, mislead readers or contain unlawful, abusive or malicious material.",
        ],
      },
      {
        heading: "Permission to use submitted content",
        paragraphs: [
          "You retain ownership of original content. By submitting it for publication, you grant Hound & Home a non-exclusive, worldwide, royalty-free licence to host, reproduce, format and display it for operating and promoting the site and products, subject to applicable law and any specific permission requested at submission.",
        ],
      },
      {
        heading: "Moderation",
        paragraphs: [
          "We may reject or remove content that breaches this policy, concerns a different product, contains personal data, appears fraudulent or is technically unsafe. We do not promise to publish every submission and will not alter a genuine review merely because it is critical.",
        ],
      },
      {
        heading: "Incentivised content",
        paragraphs: [
          "Where a reviewer received a benefit, sample or discount connected to the review, that relationship should be disclosed clearly. Hound & Home will not require a positive opinion as a condition of an incentive.",
        ],
      },
      {
        heading: "Removal requests",
        paragraphs: [
          "Contact us to request removal or correction. We will consider the request, legal recordkeeping needs and copies already shared lawfully. Removal from our active pages may not immediately remove cached copies controlled by third parties.",
        ],
      },
    ],
  },
  "website-disclaimer": {
    title: "Website Information Disclaimer",
    summary:
      "This notice explains the limits of general information published on houndandhome.xyz without limiting obligations that apply to an accepted order.",
    sections: [
      {
        heading: "General information",
        paragraphs: [
          "Care guides, articles, FAQs and other general content are provided for practical information. They are not medical, legal, financial or professional advice and may not address your individual circumstances.",
        ],
      },
      {
        heading: "Accuracy and availability",
        paragraphs: [
          "We make reasonable efforts to keep site information useful and current, but stock, pricing, compatibility and service availability can change. Product and checkout information confirmed for an accepted order takes priority over general editorial content.",
        ],
      },
      {
        heading: "External links",
        paragraphs: [
          "A link to an external site is provided for convenience and does not by itself mean we approve all of its content. External sites are controlled by their operators and should be reviewed under their own terms and privacy notices.",
        ],
      },
      {
        heading: "Consumer rights",
        paragraphs: [
          "Nothing in this disclaimer excludes product descriptions, promises or remedies that form part of a contract or that cannot be excluded under applicable consumer law.",
        ],
      },
    ],
  },
};

export const policyGroups = [
  {
    title: "Orders & payments",
    description: "Before purchase, checkout and after-sales information.",
    slugs: [
      "shipping",
      "returns",
      "refunds",
      "order-cancellations",
      "payment-security",
      "pricing-taxes",
      "preorders",
      "fraud-prevention",
    ],
  },
  {
    title: "Products & care",
    description: "Materials, safe use, maintenance and support.",
    slugs: [
      "product-information",
      "jewellery-care",
      "warranty-repairs",
      "product-safety",
    ],
  },
  {
    title: "Offers & participation",
    description: "Rules for optional offers and customer submissions.",
    slugs: ["gift-cards", "promotions", "marketing-preferences", "user-content"],
  },
  {
    title: "Privacy, access & legal",
    description: "How the site operates and the standards that apply.",
    slugs: [
      "privacy",
      "cookies",
      "terms",
      "accessibility",
      "acceptable-use",
      "intellectual-property",
      "website-disclaimer",
      "complaints",
    ],
  },
];

export const additionalPolicyRoutes = [
  "refunds",
  "order-cancellations",
  "payment-security",
  "pricing-taxes",
  "product-information",
  "jewellery-care",
  "warranty-repairs",
  "gift-cards",
  "promotions",
  "preorders",
  "fraud-prevention",
  "product-safety",
  "intellectual-property",
  "acceptable-use",
  "complaints",
  "marketing-preferences",
  "user-content",
  "website-disclaimer",
];
